/**
 * Three.js Material Nodes
 *
 * Declaration nodes: hold material params and provide buildMaterial() for
 * SceneNode to call. No payload propagation — SceneNode owns the meshes.
 */

import { BaseNode } from '@nodes/BaseNode';
import { internals, params } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { ThreeDependencies } from '../threejsPlugin';
import type { Three } from '../types';
import { rgbaToHex, DEFAULT_COLORS, type RGBA } from '../colorUtils';

// Base internals for all material nodes
const materialBaseInternals = internals({
  geometryIn: { dataType: 'three:geometry', isInput: true, label: 'Geometry' },
  meshOut: { dataType: 'three:group', isOutput: true }
});

// Shadow params shared by all materials (applied to mesh, not material)
const shadowParams = params({
  castShadow: { dataType: 'boolean', value: true, group: 'Shadow' },
  receiveShadow: { dataType: 'boolean', value: true, group: 'Shadow' }
});

type MaterialBaseInternals = typeof materialBaseInternals;
type ShadowParams = typeof shadowParams;

/**
 * Base class for material nodes — provides buildMaterial() for SceneNode.
 * Each subclass implements getMaterial() to create the specific material type.
 */
abstract class MaterialNode<T extends Record<string, any>> extends BaseNode<MaterialBaseInternals & ShadowParams & T> {
  protected three: Three;
  /** Mesh references from SceneNode / PerformanceRenderer — for in-place material mutation */
  private meshes = new Set<any>();
  /** Callback to request scene rebuild (for upstream data changes) */
  private rebuildCallback: (() => void) | null = null;
  /** Stored geometry from geometryIn — for emitting mesh on meshOut */
  private geometry: any = null;

  constructor(id: string, deps: ThreeDependencies) {
    super(id, deps);
    this.three = deps.three;
  }

  /** Build material from current params — called by SceneNode */
  buildMaterial(): any {
    return this.getMaterial();
  }

  protected abstract getMaterial(): any;

  /** Store mesh reference from SceneNode / PerformanceRenderer for in-place param mutation */
  setThreeObject(mesh: any): void {
    this.meshes.add(mesh);
  }

  /** Per-frame: re-evaluate expression params, rebuild material, push output */
  animate(): void {
    this.onParamChanged();
  }

  /** Store rebuild callback from SceneNode */
  setRebuildCallback(callback: () => void): void {
    this.rebuildCallback = callback;
  }

  onInternalChanged(payload: any, endpointId: string): void {
    if (endpointId === 'geometryIn') {
      const raw = payload && typeof payload === 'object' && 'value' in payload
        ? payload.value : payload;
      this.geometry = raw;
      this.emitMesh();
    }
    this.rebuildCallback?.();
  }

  /** Get shadow params for mesh configuration */
  getShadowConfig(): { castShadow: boolean; receiveShadow: boolean } {
    return {
      castShadow: (this.params as any).castShadow.value,
      receiveShadow: (this.params as any).receiveShadow.value
    };
  }

  onParamChanged(): void {
    if (this.meshes.size > 0) {
      const newMaterial = this.getMaterial();
      const castShadow = (this.params as any).castShadow.value;
      const receiveShadow = (this.params as any).receiveShadow.value;
      for (const mesh of this.meshes) {
        const oldMaterial = mesh.material;
        mesh.material = newMaterial;
        mesh.castShadow = castShadow;
        mesh.receiveShadow = receiveShadow;
        if (oldMaterial && oldMaterial !== newMaterial && typeof oldMaterial.dispose === 'function') {
          oldMaterial.dispose();
        }
      }
    }
    this.emitMesh();
  }

  /** Build a mesh from stored geometry + current material and emit on meshOut */
  private emitMesh(): void {
    if (!this.geometry) return;
    const material = this.getMaterial();
    const mesh = new this.three.Mesh(this.geometry, material);
    mesh.castShadow = (this.params as any).castShadow.value;
    mesh.receiveShadow = (this.params as any).receiveShadow.value;
    (this.internals as any).meshOut.set({ value: mesh });
  }

  dispose(): void {
    this.geometry = null;
    this.meshes.clear();
    this.rebuildCallback = null;
    super.dispose();
  }
}

// ─────────────────────────────────────────────────────────────
// Basic Material (unlit)
// ─────────────────────────────────────────────────────────────

const basicMaterialParams = params({
  color: { dataType: 'color', value: DEFAULT_COLORS.white },
  wireframe: { dataType: 'boolean', value: false },
  opacity: { value: 1, min: 0, max: 1, step: 0.01 },
  transparent: { dataType: 'boolean', value: false }
});

const basicMaterialEndpoints = {
  ...materialBaseInternals,
  ...shadowParams,
  ...basicMaterialParams
};

class BasicMaterialNode extends MaterialNode<typeof basicMaterialParams> {
  protected getMaterial() {
    return new this.three.MeshBasicMaterial({
      color: rgbaToHex(this.params.color.value as RGBA),
      wireframe: this.params.wireframe.value,
      opacity: this.params.opacity.value,
      transparent: this.params.transparent.value
    });
  }
}

// ─────────────────────────────────────────────────────────────
// Standard Material (PBR)
// ─────────────────────────────────────────────────────────────

const standardMaterialParams = params({
  color: { dataType: 'color', value: DEFAULT_COLORS.white },
  metalness: { value: 0.5, min: 0, max: 1, step: 0.01 },
  roughness: { value: 0.5, min: 0, max: 1, step: 0.01 },
  wireframe: { dataType: 'boolean', value: false },
  flatShading: { dataType: 'boolean', value: false, label: 'Flat Shading' },
  vertexColors: { dataType: 'boolean', value: false, label: 'Vertex Colors' },
  opacity: { value: 1, min: 0, max: 1, step: 0.01 },
  transparent: { dataType: 'boolean', value: false }
});

const standardMaterialEndpoints = {
  ...materialBaseInternals,
  ...shadowParams,
  ...standardMaterialParams
};

class StandardMaterialNode extends MaterialNode<typeof standardMaterialParams> {
  protected getMaterial() {
    return new this.three.MeshStandardMaterial({
      color: rgbaToHex(this.params.color.value as RGBA),
      metalness: this.params.metalness.value,
      roughness: this.params.roughness.value,
      wireframe: this.params.wireframe.value,
      flatShading: this.params.flatShading.value,
      vertexColors: this.params.vertexColors.value,
      opacity: this.params.opacity.value,
      transparent: this.params.transparent.value
    });
  }
}

// ─────────────────────────────────────────────────────────────
// Phong Material (shiny with specular)
// ─────────────────────────────────────────────────────────────

const phongMaterialParams = params({
  color: { dataType: 'color', value: DEFAULT_COLORS.white },
  specular: { dataType: 'color', value: DEFAULT_COLORS.darkGray },
  shininess: { value: 30, min: 0, max: 100, step: 1 },
  wireframe: { dataType: 'boolean', value: false },
  flatShading: { dataType: 'boolean', value: false, label: 'Flat Shading' },
  opacity: { value: 1, min: 0, max: 1, step: 0.01 },
  transparent: { dataType: 'boolean', value: false }
});

const phongMaterialEndpoints = {
  ...materialBaseInternals,
  ...shadowParams,
  ...phongMaterialParams
};

class PhongMaterialNode extends MaterialNode<typeof phongMaterialParams> {
  protected getMaterial() {
    return new this.three.MeshPhongMaterial({
      color: rgbaToHex(this.params.color.value as RGBA),
      specular: rgbaToHex(this.params.specular.value as RGBA),
      shininess: this.params.shininess.value,
      wireframe: this.params.wireframe.value,
      flatShading: this.params.flatShading.value,
      opacity: this.params.opacity.value,
      transparent: this.params.transparent.value
    });
  }
}

// ─────────────────────────────────────────────────────────────
// Normal Material (debug visualization)
// ─────────────────────────────────────────────────────────────

const normalMaterialParams = params({
  wireframe: { dataType: 'boolean', value: false },
  flatShading: { dataType: 'boolean', value: false, label: 'Flat Shading' }
});

const normalMaterialEndpoints = {
  ...materialBaseInternals,
  ...shadowParams,
  ...normalMaterialParams
};

class NormalMaterialNode extends MaterialNode<typeof normalMaterialParams> {
  protected getMaterial() {
    return new this.three.MeshNormalMaterial({
      wireframe: this.params.wireframe.value,
      flatShading: this.params.flatShading.value
    });
  }
}

export const materialNodes: NodeDef[] = [
  {
    type: 'BasicMaterial',
    shortName: 'basic',
    category: 'material',
    endpoints: basicMaterialEndpoints,
    ctor: BasicMaterialNode
  },
  {
    type: 'StandardMaterial',
    shortName: 'standard',
    category: 'material',
    endpoints: standardMaterialEndpoints,
    ctor: StandardMaterialNode
  },
  {
    type: 'PhongMaterial',
    shortName: 'phong',
    category: 'material',
    endpoints: phongMaterialEndpoints,
    ctor: PhongMaterialNode
  },
  {
    type: 'NormalMaterial',
    shortName: 'normal',
    category: 'material',
    endpoints: normalMaterialEndpoints,
    ctor: NormalMaterialNode
  }
];
